import './styles/ModulesList.scss';

import { useApolloClient, useMutation, useQuery as useGQLQuery } from '@apollo/client';
import DoDisturbAltIcon from '@mui/icons-material/DoDisturbAlt';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import { CircularProgress, Grid, Icon, Tooltip } from '@mui/material';
import { GridActionsCellItem } from '@mui/x-data-grid';
import { useQueryClient } from '@tanstack/react-query';
import { Module } from '@thro-types/modules-selections';

import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import MUTATION from 'gql/mutations/ModulesService/deleteModule.gql';
import UPDATE_STATE from 'gql/mutations/ModulesService/updateModule.gql';
import QUERY_GROUPINGS from 'gql/queries/ModulesService/getGroupings.gql';
import USER_QUERY from 'gql/queries/currentUser.gql';

import { invalidateDefaultModulesQuery, useDefaultModulesQuery } from 'api/hooks/modules';
import { useCoursesQuery } from 'api/hooks/useCoursesQuery';
import { ConfirmationDialog } from 'app/components/ConfirmationDialog';
import { CreateButton } from 'app/components/CreateButton';
import ErrorBoundary from 'app/components/ErrorBoundary';
import { Filter } from 'app/components/Filters';
import GQLGuard from 'app/components/GQLGuard';
import { Modal } from 'app/components/Modal';
import { ActiveState, ModuleItem } from 'app/components/RichList/items/AWPM-FWPM/ModuleItem';
import {
  getModuleLecturer,
  isModuleActive
} from 'app/components/RichList/items/AWPM-FWPM/ModuleItem/helper';
import { TilesOrListView, TypedGridColumns } from 'app/components/TilesOrListView';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';
import { SortOption } from 'types/AppTypes';
import { ACLHelper } from 'utils/ACLHelper';
import { useCurrentURL } from 'utils/hooks/useCurrentURL';
import usePageHeading from 'utils/hooks/usePageHeading';

import { SubjectType } from '../../ModuleSelections/models';
import { getSeatsForModules } from '../../helper';
import ModuleModal from '../ModuleModal';
import { ModuleWithComplete } from '../models';

interface Props {
  subjectType: SubjectType;
}

export function ModulesList(props: Props) {
  const [deleteModule] = useMutation(MUTATION, {
    context: { clientName: 'federation' }
  });
  const titleInputState = useState('');
  const groupingInputState = useState([]);
  const activeStateInputState = useState([]);
  const completionInputState = useState<ActiveState | null>();
  const creatorFilterInputState = useState<number | undefined>(undefined);
  const [sortState, setSortState] = useState<'asc' | 'desc'>('asc');
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const client = useApolloClient();
  const queryClient = useQueryClient();
  const { currentUser } = client.readQuery({ query: USER_QUERY });
  const aclHelper = new ACLHelper(currentUser);
  const currentURL = useCurrentURL();
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [disabledCancel, setDisabledCancel] = useState(false);
  const [currentlyEditingModule, setCurrentlyEditingModule] = useState<Module | undefined>(
    undefined
  );
  const [changeInactiveStateModalOpen, setChangeInactiveStateModalOpen] = useState(false);
  const [changeActiveStateModalOpen, setChangeActiveStateModalOpen] = useState(false);

  const [updateState] = useMutation(UPDATE_STATE, {
    context: { clientName: 'federation' }
  });

  usePageHeading({
    heading: t(translations.pages.modules.title[props.subjectType.toLowerCase()]()),
    mobilePadding: false
  });

  const { data: courses } = useCoursesQuery();

  const handleChangeState = async () => {
    if (!currentlyEditingModule) {
      return;
    }
    const { errors } = await updateState({
      variables: {
        id: Number(currentlyEditingModule.id),
        data: {
          activeState: isModuleActive(currentlyEditingModule)
            ? ActiveState.Inactive
            : ActiveState.Active
        }
      }
    });
    invalidateDefaultModulesQuery(queryClient);
    if (errors) {
      dispatch(AlertActions.error(t(translations.pages.modules.alerts.changeStateFailed())));
    } else {
      dispatch(AlertActions.success(t(translations.pages.modules.alerts.changeStateSuccssful())));
    }
  };

  const { data: modules, isLoading } = useDefaultModulesQuery({
    params: {
      subjectType: props.subjectType,
      nameSort: sortState,
      courses:
        !aclHelper.isSuperUser() && props.subjectType === SubjectType.Fwpm
          ? aclHelper.getSelectionAdminCourses(courses)?.map(course => course.id)
          : undefined,
      activeState: activeStateInputState[0],
      name: titleInputState[0] ? titleInputState[0] : undefined,
      groupings: groupingInputState[0],
      completionState: completionInputState[0],
      creatorId: creatorFilterInputState[0]
    }
  });

  let ableToCreate = false;
  if (props.subjectType === SubjectType.Awpm) {
    if (aclHelper.isSelectionAdmin() && aclHelper.isSubjectSelectionAdmin(true, SubjectType.Awpm)) {
      ableToCreate = true;
    }
  } else if (props.subjectType === SubjectType.Fwpm) {
    if (aclHelper.isSelectionAdmin()) {
      ableToCreate = true;
    }
  }
  if (aclHelper.isSuperUser()) {
    ableToCreate = true;
  }

  const getGroupings = useGQLQuery<any, any>(QUERY_GROUPINGS, {
    context: { clientName: 'federation' }
  });

  const cancelModuleCreation = (_event?, reason?) => {
    if (disabledCancel || (reason && reason === 'backdropClick')) {
      return;
    }
    setCurrentlyEditingModule(undefined);
    setDisabledCancel(true);
    setCreateModalOpen(false);
    setDisabledCancel(false);
    invalidateDefaultModulesQuery(queryClient);
  };

  const handleDelete = async () => {
    const { errors } = await deleteModule({
      variables: {
        id: Number(currentlyEditingModule?.id)
      },
      refetchQueries: ['GetDefaultModules']
    });
    if (errors) {
      dispatch(AlertActions.error(t(translations.pages.modules.alerts.deleteFailed())));
    } else {
      dispatch(AlertActions.success(t(translations.pages.modules.alerts.deleteSuccessful())));
      invalidateDefaultModulesQuery(queryClient);
    }
  };

  const richlistFilter = (groupings: any): Filter[] => [
    {
      field: 'name',
      label: t(translations.pages.articles.headline()),
      stateHandler: titleInputState
    },
    {
      field: 'activeState',
      label: t(translations.pages.modules.status()),
      select: {
        options: [
          { label: '---', name: null },
          { label: t(translations.pages.modules.activated()), name: 'ACTIVE' },
          { label: t(translations.pages.modules.deactivated()), name: 'INACTIVE' }
        ],
        stateHandler: activeStateInputState
      }
    },
    {
      field: 'groupings',
      label: t(translations.pages.modules.grouping()),
      multiselect: {
        options: groupings.map(grouping => {
          return { label: grouping.name, name: parseInt(grouping.id, 10) };
        }),
        stateHandler: groupingInputState,
        customStyle: { width: '200px' }
      },
      hide: props.subjectType === 'FWPM'
    },
    {
      field: 'completionState',
      label: t(translations.pages.moduleSelections.condition()),
      select: {
        options: [
          { label: '---', name: null },
          { label: t(translations.pages.modules.complete()), name: 'completed' },
          { label: t(translations.pages.modules.draft()), name: 'draft' }
        ],
        stateHandler: completionInputState
      }
    },
    {
      field: 'creatorId',
      label: t(translations.pages.studentView.myModules()),
      toggle: { value: currentUser.id, color: 'archived' }
    }
  ];

  const sortOptions: SortOption[] = [
    {
      label: t(translations.pages.moduleSelections.sort.nameAsc()),
      value: { nameSort: 'asc' }
    },
    {
      label: t(translations.pages.moduleSelections.sort.nameDesc()),
      value: { nameSort: 'desc' }
    }
  ];

  const onSortChange = sortings => {
    setSortState(sortings['nameSort']);
  };

  const rowActions = useCallback(
    params => {
      return [
        <GridActionsCellItem
          icon={
            <Icon
              className="far fa-edit"
              style={{
                width: 'auto',
                color: 'lightblue'
              }}
            />
          }
          label={t(translations.common.edit())}
          onClick={() => {
            setCurrentlyEditingModule(params.row);
            setCreateModalOpen(true);
          }}
          showInMenu
        />,
        <GridActionsCellItem
          icon={
            <Icon
              className="far fa-trash-alt"
              style={{
                width: 'auto',
                color: 'red'
              }}
            />
          }
          label={t(translations.common.delete())}
          onClick={() => {
            setCurrentlyEditingModule(params.row);
            setDeleteModalOpen(true);
          }}
          showInMenu
        />,
        <GridActionsCellItem
          icon={
            isModuleActive(params.row) ? (
              <DoDisturbAltIcon
                style={{
                  width: 'auto',
                  color: 'grey',
                  fontSize: '1.1em'
                }}
              />
            ) : (
              <RadioButtonCheckedIcon
                style={{
                  width: 'auto',
                  color: 'green',
                  fontSize: '1.1em'
                }}
              />
            )
          }
          label={
            isModuleActive(params.row)
              ? t(translations.pages.modules.deactivate())
              : t(translations.pages.modules.activate())
          }
          onClick={() => {
            setCurrentlyEditingModule(params.row);
            isModuleActive(params.row)
              ? setChangeInactiveStateModalOpen(true)
              : setChangeActiveStateModalOpen(true);
          }}
          showInMenu
        />
      ];
    },
    [t]
  );

  const columns = useMemo((): TypedGridColumns<
    ModuleWithComplete & { lecturersUsers: string[] }
  > => {
    return [
      {
        field: 'complete',
        flex: 2,
        headerName: t(translations.pages.modules.status()),
        valueGetter(value, row) {
          return row.complete
            ? t(translations.pages.modules.statusComplete())
            : t(translations.pages.modules.statusIncomplete());
        }
      },
      {
        flex: 3,
        minWidth: 200,
        headerName: t(translations.pages.modules.create.modulePreview.title()),
        field: 'name',
        renderCell: params => (
          <Tooltip title={params.row.name}>
            <span>{params.row.name}</span>
          </Tooltip>
        )
      },
      {
        flex: 2,
        minWidth: 200,
        headerName: t(translations.pages.modules.lecturer()),
        field: 'lecturersUsers',
        renderCell: params => (
          <Tooltip title={getModuleLecturer(params.row, t)}>
            <span>{getModuleLecturer(params.row, t)}</span>
          </Tooltip>
        )
      },
      {
        flex: 2,
        minWidth: 200,
        headerName: t(translations.pages.modules.grouping()),
        field: 'grouping',
        valueGetter: (value, row) => row?.grouping?.name,
        renderCell: params => (
          <Tooltip title={params.row?.grouping?.name}>
            <span>{params.row?.grouping?.name}</span>
          </Tooltip>
        )
      },
      {
        flex: 2,
        minWidth: 200,
        headerName: t(translations.pages.modules.seatsAvailable()),
        field: 'maxParticipants',
        renderCell: params => (
          <Tooltip title={params.row.maxParticipants}>
            <>
              <span className="module__footer__seats">{params.row.maxParticipants}</span>
              <div style={{ whiteSpace: 'normal' }}>{getSeatsForModules(params, courses, t)}</div>
            </>
          </Tooltip>
        )
      },
      {
        flex: 1,
        minWidth: 100,
        headerName: t(translations.common.actions()),
        field: 'actions',
        type: 'actions',
        getActions: rowActions
      }
    ];
  }, [t, rowActions, courses]);

  return (
    <>
      <ConfirmationDialog
        open={deleteModalOpen}
        title={t(translations.pages.modules.deleteConfirm.title())}
        onClose={() => {
          setDeleteModalOpen(false);
        }}
        onConfirm={() => {
          setDeleteModalOpen(false);
          handleDelete();
        }}
      >
        <div>{t(translations.pages.modules.deleteConfirm.message())}</div>
      </ConfirmationDialog>
      <ConfirmationDialog
        open={cancelModalOpen}
        title={t(translations.common.cancelConfirm.title())}
        onClose={() => {
          setCancelModalOpen(false);
        }}
        onConfirm={() => {
          setCreateModalOpen(false);
          setCancelModalOpen(false);
        }}
      >
        <div>{t(translations.common.cancelConfirm.message())}</div>
      </ConfirmationDialog>
      <ConfirmationDialog
        open={changeActiveStateModalOpen}
        title={t(translations.pages.modules.setStateConfirm.setActiveTitle())}
        onClose={() => {
          setChangeActiveStateModalOpen(false);
        }}
        onConfirm={() => {
          setChangeActiveStateModalOpen(false);
          handleChangeState();
        }}
      >
        <div>{t(translations.pages.modules.setStateConfirm.setActive())}</div>
      </ConfirmationDialog>
      <ConfirmationDialog
        open={changeInactiveStateModalOpen}
        title={t(translations.pages.modules.setStateConfirm.setInavctiveTitle())}
        onClose={() => {
          setChangeInactiveStateModalOpen(false);
        }}
        onConfirm={() => {
          setChangeInactiveStateModalOpen(false);
          handleChangeState();
        }}
      >
        <div>{t(translations.pages.modules.setStateConfirm.setInactive())}</div>
      </ConfirmationDialog>
      <Grid container>
        <ErrorBoundary boundaryTag="listingsListBoundary">
          {isLoading ? (
            <CircularProgress />
          ) : (
            <GQLGuard
              query={[getGroupings]}
              onError={error => {
                if (error?.graphQLErrors[0]?.extensions?.status === 404) {
                  dispatch(
                    AlertActions.error(`${t(translations.pages.modules.alerts.failedLoading())}`)
                  );
                } else {
                  dispatch(
                    AlertActions.error(
                      `${t(translations.pages.modules.alerts.failedLoading())}: ${error.message}`
                    )
                  );
                }
                navigate('/', { replace: true });
                return null;
              }}
            >
              {([groupingsData]) => (
                <>
                  <Grid style={{ width: '100%' }}>
                    <TilesOrListView
                      filters={richlistFilter(groupingsData.getGroupings)}
                      sortOptions={sortOptions}
                      onSortChange={onSortChange}
                      onFilterChange={variables => {
                        const filter = JSON.parse(variables.filter);
                        filter.creatorId
                          ? creatorFilterInputState[1](filter.creatorId.term[0])
                          : creatorFilterInputState[1](undefined);
                      }}
                      columns={columns}
                      height={'70vh'}
                      data={modules || []}
                      renderTile={l => (
                        <ModuleItem
                          module={l}
                          subjectType={props.subjectType}
                          onEdit={() => invalidateDefaultModulesQuery(queryClient)}
                          onDelete={() => invalidateDefaultModulesQuery(queryClient)}
                        />
                      )}
                      controlElements={
                        ableToCreate && (
                          <Grid item>
                            <CreateButton
                              onClick={() => {
                                setCreateModalOpen(true);
                              }}
                            >
                              {t(translations.common.create())}
                            </CreateButton>
                          </Grid>
                        )
                      }
                      onRowClick={event => {
                        navigate(
                          `/${event.row.subjectType.toLowerCase()}/modules/${event.row.id}`,
                          {
                            state: { back: currentURL }
                          }
                        );
                      }}
                      getRowClassName={params => {
                        return isModuleActive(params.row) ? '' : 'module-inactive';
                      }}
                      columnVisibilityModel={{ grouping: props.subjectType !== SubjectType.Fwpm }}
                    />
                  </Grid>
                  <ErrorBoundary boundaryTag="articleDetailBoundary">
                    <Modal
                      open={createModalOpen}
                      onClose={(_event, reason) => {
                        if (reason && reason === 'backdropClick') return;
                        setCancelModalOpen(true);
                      }}
                      headline={t(
                        translations.pages.modules[
                          !currentlyEditingModule?.id ? 'create' : 'edit'
                        ].title[props.subjectType.toLowerCase()]()
                      )}
                      onCancel={(_event, reason) => {
                        if (reason && reason === 'backdropClick') return;
                        setCancelModalOpen(true);
                      }}
                      disabledCancel={disabledCancel}
                    >
                      <ModuleModal
                        closeHandler={cancelModuleCreation}
                        setDisabledCancel={setDisabledCancel}
                        subjectType={props.subjectType}
                        module={currentlyEditingModule}
                        courses={courses}
                      />
                    </Modal>
                  </ErrorBoundary>
                </>
              )}
            </GQLGuard>
          )}
        </ErrorBoundary>
      </Grid>
    </>
  );
}
